// Tremor Select [v1.0.0]
import React, { useMemo, useState } from 'react'
import * as SelectPrimitives from '@radix-ui/react-select'
import { IconChevronDown, IconChevronUp, IconCheck, IconSelector } from '@/components/icons'
import { Input } from '@/components/Input'

import { cx, focusInput, hasErrorInput } from '@/lib/utils'

const Select = SelectPrimitives.Root
Select.displayName = 'Select'

const SelectGroup = SelectPrimitives.Group
SelectGroup.displayName = 'SelectGroup'

const SelectValue = SelectPrimitives.Value
SelectValue.displayName = 'SelectValue'

const selectTriggerStyles = [
  // base
  'group/trigger flex w-full select-none items-center justify-between gap-2 truncate rounded-md border px-3 py-2 shadow-xs outline-hidden transition sm:text-sm',
  // border color
  'border-gray-300',
  // text color
  'text-gray-900',
  // placeholder
  'data-placeholder:text-gray-500',
  // background color
  'bg-white',
  // hover — keep the white background (blending into the page bg read as broken/disabled)
  // and use a shadow instead of a color shift to signal interactivity
  'hover:shadow',
  // disabled
  'data-disabled:bg-gray-100 data-disabled:text-gray-400',
  ...focusInput,
]

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Trigger> & {
    hasError?: boolean
    /** Renders in place of the default truncated single-line wrapper, e.g. for multi-line custom content. */
    renderValue?: React.ReactNode
  }
>(({ className, hasError, children, renderValue, ...props }, forwardedRef) => {
  return (
    <SelectPrimitives.Trigger
      ref={forwardedRef}
      className={cx(selectTriggerStyles, hasError ? hasErrorInput : '', className)}
      {...props}
    >
      {renderValue ?? <span className="truncate">{children}</span>}
      <SelectPrimitives.Icon asChild>
        <IconSelector className="size-4 shrink-0 text-gray-400 group-data-disabled/trigger:text-gray-300" />
      </SelectPrimitives.Icon>
    </SelectPrimitives.Trigger>
  )
})

SelectTrigger.displayName = 'SelectTrigger'

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.ScrollUpButton>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.ScrollUpButton
    ref={forwardedRef}
    className={cx('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <IconChevronUp className="size-3 shrink-0" aria-hidden="true" />
  </SelectPrimitives.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitives.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.ScrollDownButton>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.ScrollDownButton
    ref={forwardedRef}
    className={cx('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <IconChevronDown className="size-3 shrink-0" aria-hidden="true" />
  </SelectPrimitives.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitives.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Content>
>(({ className, position = 'popper', children, sideOffset = 8, collisionPadding = 10, ...props }, forwardedRef) => (
  <SelectPrimitives.Portal>
    <SelectPrimitives.Content
      ref={forwardedRef}
      className={cx(
        // base
        'relative z-50 overflow-hidden rounded-md border shadow-xl shadow-black/[2.5%]',
        // widths
        'min-w-[calc(var(--radix-select-trigger-width)-2px)] max-w-[95vw]',
        // heights
        'max-h-(--radix-select-content-available-height)',
        // background color
        'bg-white',
        // text color
        'text-gray-900',
        // border color
        'border-gray-200',
        // transition
        'will-change-[transform,opacity]',
        'data-[state=closed]:animate-hide',
        'data-[side=bottom]:animate-slide-down-and-fade data-[side=left]:animate-slide-left-and-fade data-[side=right]:animate-slide-right-and-fade data-[side=top]:animate-slide-up-and-fade',
        className,
      )}
      sideOffset={sideOffset}
      position={position}
      collisionPadding={collisionPadding}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitives.Viewport
        className={cx(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[calc(var(--radix-select-trigger-width))]',
        )}
      >
        {children}
      </SelectPrimitives.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitives.Content>
  </SelectPrimitives.Portal>
))

SelectContent.displayName = 'SelectContent'

const SelectGroupLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Label>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.Label
    ref={forwardedRef}
    className={cx('px-3 py-2 text-xs font-medium tracking-wide text-gray-500', className)}
    {...props}
  />
))

SelectGroupLabel.displayName = 'SelectGroupLabel'

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Item>
>(({ className, children, ...props }, forwardedRef) => {
  return (
    <SelectPrimitives.Item
      ref={forwardedRef}
      className={cx(
        // base
        'grid cursor-pointer grid-cols-[1fr_20px] gap-x-2 rounded-sm px-3 py-2 outline-hidden transition-colors data-[state=checked]:font-semibold sm:text-sm',
        // text color
        'text-gray-900',
        // disabled
        'data-disabled:pointer-events-none data-disabled:text-gray-400 data-disabled:hover:bg-none',
        // focus
        'focus-visible:bg-gray-100',
        // hover
        'hover:bg-gray-100',
        className,
      )}
      {...props}
    >
      <SelectPrimitives.ItemText className="flex-1 truncate">{children}</SelectPrimitives.ItemText>
      <SelectPrimitives.ItemIndicator>
        <IconCheck className="size-5 shrink-0 text-gray-800" aria-hidden="true" />
      </SelectPrimitives.ItemIndicator>
    </SelectPrimitives.Item>
  )
})

SelectItem.displayName = 'SelectItem'

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Separator>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.Separator ref={forwardedRef} className={cx('-mx-1 my-1 h-px bg-gray-300', className)} {...props} />
))

SelectSeparator.displayName = 'SelectSeparator'

// Radix Select.Item requires a non-empty value, so "All X" filter options (Clip Archive,
// Project Tracker, ...) use this sentinel instead of ''. Double-underscore wrapped so it
// can't collide with a real option value that happens to use its own name as the value
// (e.g. Shot Style, which has no separate id).
export const FILTER_ALL_VALUE = '__all__'

export function FilterSelect({
  value,
  onValueChange,
  placeholder,
  options,
}: {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  options: { value: string; label: string }[]
}) {
  return (
    <Select value={value || FILTER_ALL_VALUE} onValueChange={(v) => onValueChange(v === FILTER_ALL_VALUE ? '' : v)}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={FILTER_ALL_VALUE}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// Same as FilterSelect, plus a search input pinned above the option list — for dropdowns whose
// option count makes scroll-to-find impractical (e.g. Project Tracker's Users/Regions filters).
// `searchText`, if provided, lets an option match on more than its visible label (e.g. a user's
// email as well as their name) without changing what's actually displayed.
//
// Two modes, chosen by whether `remote` is passed:
// - Default (no `remote`): `options` is the full list, filtered client-side as the user types.
//   Fine for anything whose full set is already loaded and small (regions/groups, campaigns).
// - `remote`: the caller owns search state and re-fetches `options` itself (e.g. a debounced
//   server query) — used for Users, since the client-side list source (a `calculated` aggregate)
//   is capped at 500 rows server-side and silently drops alphabetically-later names on large
//   teams (ADM-609). In this mode `options` is trusted as-is (already filtered by the caller,
//   via a real search) rather than filtered again against `search`/`searchText` here.
export function SearchableFilterSelect({
  value,
  onValueChange,
  placeholder,
  searchPlaceholder = 'Search...',
  options,
  remote,
}: {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  searchPlaceholder?: string
  options: { value: string; label: string; searchText?: string }[]
  remote?: {
    search: string
    onSearchChange: (search: string) => void
    loading?: boolean
    /** Shown instead of "No matches" while the user hasn't typed anything yet. */
    emptySearchHint?: string
  }
}) {
  const [localSearch, setLocalSearch] = useState('')
  const search = remote ? remote.search : localSearch
  const setSearch = remote ? remote.onSearchChange : setLocalSearch

  const filteredOptions = useMemo(() => {
    if (remote) return options
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => (o.searchText ?? o.label).toLowerCase().includes(q))
  }, [options, search, remote])

  const emptyMessage = remote && !remote.search.trim() ? (remote.emptySearchHint ?? 'Type to search') : 'No matches'

  return (
    <Select
      value={value || FILTER_ALL_VALUE}
      onValueChange={(v) => onValueChange(v === FILTER_ALL_VALUE ? '' : v)}
      onOpenChange={(open) => {
        if (!open) setSearch('')
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {/* Radix Select's built-in type-ahead would otherwise intercept these keystrokes to
            jump-select a matching item by first letter — stopping propagation here keeps them
            local to the search input instead. Also blocks Home/End/PageUp/PageDown, which Radix
            uses to move focus into the item list; without stopping them the cursor jumps out of
            the input on those keys. */}
        <div className="p-1" onKeyDown={(e) => e.stopPropagation()}>
          <Input
            type="search"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="mb-1"
          />
        </div>
        <SelectItem value={FILTER_ALL_VALUE}>{placeholder}</SelectItem>
        {remote?.loading ? (
          <p className="px-3 py-2 text-sm text-gray-500">Searching...</p>
        ) : filteredOptions.length === 0 ? (
          <p className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</p>
        ) : (
          filteredOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  )
}

export { Select, SelectContent, SelectGroup, SelectGroupLabel, SelectItem, SelectSeparator, SelectTrigger, SelectValue }
